import { inject, computed, getCurrentInstance, watch, toRaw, unref } from 'vue'
import { get } from 'lodash-unified'
import { isObject as _isObject, escapeStringRegexp } from '@hongluan-ui/utils'
import { selectKey, selectGroupKey } from './token'

import type { Ref } from 'vue'
import type { QueryChangeCtx } from './token'

export function useOption(props: any, states: any) {
  // inject
  const select = inject(selectKey)!
  const selectGroup = inject(selectGroupKey, { disabled: false })

  const isObject = computed(() => _isObject(props.value))

  const itemSelected = computed(() => {
    if (!select.props.multiple) {
      return isEqual(props.value, select.props.modelValue)
    } else {
      return contains(select.props.modelValue as unknown[], props.value)
    }
  })

  const limitReached = computed(() => {
    if (select.props.multiple) {
      const modelValue = (select.props.modelValue || []) as unknown[]
      return !itemSelected.value &&
      modelValue.length >= select.props.multipleLimit! &&
        select.props.multipleLimit! > 0
    } else {
      return false
    }
  })

  const currentLabel = computed(() => {
    return props.label || (isObject.value ? '' : props.value)
  })

  const currentValue = computed(() => {
    return props.value || props.label || ''
  })

  const isDisabled = computed(() => {
    return props.disabled || states.groupDisabled || limitReached.value
  })

  const instance = getCurrentInstance()!

  const contains = (arr: unknown[] = [], target: unknown) => {
    if (!isObject.value) {
      return arr && arr.indexOf(target) > -1
    } else {
      const valueKey = select.props.valueKey!
      return arr && arr.some(item => {
        return toRaw(get(item, valueKey)) === get(target, valueKey)
      })
    }
  }

  const isEqual = (a: unknown, b: unknown) => {
    if (!isObject.value) {
      return a === b
    } else {
      const { valueKey } = select.props
      return get(a, valueKey!) === get(b, valueKey!)
    }
  }

  const hoverItem = () => {
    if (!props.disabled && !selectGroup.disabled) {
      select.hoverIndex = select.optionsArray.indexOf(instance.proxy)
    }
  }

  watch(() => currentLabel.value, () => {
    if (!props.created && !select.props.remote) select.setSelected()
  })

  watch(() => props.value, (val, oldVal) => {
    const { remote, valueKey } = select.props
    if (!Object.is(val, oldVal)) {
      select.onOptionDestroy(oldVal, instance.proxy as any)
      select.onOptionCreate(instance.proxy as any)
    }

    if (!props.created && !remote) {
      if (valueKey && _isObject(val) && _isObject(oldVal) && val[valueKey] === oldVal[valueKey]) {
        return
      }
      select.setSelected()
    }
  })

  watch(() => selectGroup.disabled, () => {
    states.groupDisabled = selectGroup.disabled
  }, { immediate: true })

  const { queryChange } = toRaw(select)
  watch(
    queryChange,
    (changes: Ref<QueryChangeCtx>) => {
      const { query } = unref(changes)

      const regexp = new RegExp(escapeStringRegexp(query), 'i')
      states.visible = regexp.test(currentLabel.value) || props.created
      if (!states.visible) {
        select.filteredOptionsCount--
      }
    },
    { immediate: true },
  )

  return {
    select,
    currentLabel,
    currentValue,
    itemSelected,
    isDisabled,
    hoverItem,
  }
}
